import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import axios from "axios";
import config from '../../config';

import '../../components/dashboard/styles/dashboard.css';

import PageHeader from "../../components/general/pageHeader";
import DashboardHeader from "../../components/dashboard/managerComponents/dashboardHeader";
import MobileHeader from "../../components/dashboard/managerComponents/mobileHeader.js";
import RequestStatus from '../../components/dashboard/managerComponents/requestStatus';
import VisitorsParcelsWeek from '../../components/dashboard/managerComponents/visitorsParcelsWeek';
import UnitsOutstanding from '../../components/dashboard/managerComponents/unitsOutstanding';
import RecentInfo from '../../components/dashboard/managerComponents/recentInfo';
import useAuthenticate from "../../components/authentication/authenticate";

// import DataQualityGraph from '../../components/dashboard/managerComponents/dataQuality';
// import DataUtilizationGraph from '../../components/dashboard/managerComponents/dataUtilization';
// import AccountsReceivable from '../../components/dashboard/accountsReceivable';

import Loading from "../../components/general/loading";

function Dashboard() {
  const [selectedProperty, setSelectedProperty] = React.useState(null);

  const { user, isLoading: isUserLoading } = useAuthenticate();

  const { data: propertiesData, isLoading: isPropertiesLoading } = useQuery({
    queryKey: ['properties'],
    queryFn: fetchProperties
  });

  const { data: unitsData, isLoading: isUnitsLoading } = useQuery({
    queryKey: ['units', selectedProperty?._id],
    queryFn: () => fetchUnits(selectedProperty?._id),
    enabled: !!selectedProperty?._id
  });

  const { data: visitorsData, isLoading: isVisitorsLoading } = useQuery({
    queryKey: ['visitors', selectedProperty?._id],
    queryFn: () => fetchVisitors(selectedProperty?._id),
    enabled: !!selectedProperty?._id
  });

  const { data: requestsData, isLoading: isRequestsLoading } = useQuery({
    queryKey: ['requests', selectedProperty?._id],
    queryFn: () => fetchRequests(selectedProperty?._id),
    enabled: !!selectedProperty?._id
  });

  const { data: paymentsData, isLoading: isPaymentsLoading } = useQuery({
    queryKey: ['paymentsData', selectedProperty?._id],
    queryFn: () => fetchPayments(selectedProperty?._id),
    enabled: !!selectedProperty?._id
  });

  const { data: unitsOutstandingData, isLoading: isUnitsOutstandingLoading } = useQuery({
    queryKey: ['unitsOutstanding', selectedProperty?._id],
    queryFn: () => fetchUnitsOutstandingBalances(selectedProperty?._id),
    enabled: !!selectedProperty?._id
  });

  /* const { data: accountsOutstandingData } = useQuery({
    queryKey: ['accountsOutstanding', selectedProperty?._id],
    queryFn: () => fetchAccountsOutstandingBalances(selectedProperty?._id),
    enabled: !!selectedProperty?._id
  }); */

  // Always select the first property when propertiesData is loaded
  useEffect(() => {
    if (propertiesData?.length > 0) {
      setSelectedProperty(propertiesData[0]);
    }
  }, [propertiesData]);

  const isAnyLoading =
    !selectedProperty ||
    isUserLoading ||
    isPropertiesLoading ||
    isUnitsLoading ||
    isVisitorsLoading ||
    isRequestsLoading ||
    isPaymentsLoading ||
    isUnitsOutstandingLoading;

  if (isAnyLoading) return <Loading />;

  return (
    <div className="dashboard-container w-full">
      <PageHeader pageName="Dashboard" />
      <div className="hidden lg:flex">
        <DashboardHeader
          userData={user}
          propertiesData={propertiesData}
          unitsData={unitsData}
          selectedProperty={selectedProperty}
          onPropertyChange={setSelectedProperty}
        />
      </div>
      <div className="lg:hidden">
        <MobileHeader
          userData={user}
          propertiesData={propertiesData}
          unitsData={unitsData}
          selectedProperty={selectedProperty}
          onPropertyChange={setSelectedProperty}
        />
      </div>

      <div className="grid  grid-cols-1 lg:grid-cols-2 gap-[20px]">
        <div className="square-item">
          <RequestStatus data={requestsData} />
        </div>
        <div className="square-item">
          <VisitorsParcelsWeek data={visitorsData} />
        </div>
      </div>

      <div className="dashboard-row" style={{ overflowY: "auto", maxHeight: "350px" }}>
        <UnitsOutstanding data={unitsOutstandingData} />
      </div>

      <div className="dashboard-row" style={{ minHeight: "300px" }}>
        <RecentInfo visitorsData={visitorsData} requestsData={requestsData} paymentsData={paymentsData} />
      </div>

    </div>
  );
}

const fetchProperties = async () => {
  const API_URL = config.API_URL || 'http://localhost:5000';

  try {
    const response = await axios.get(`${API_URL}/v2/property`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching properties:", error);
    return [];
  }
};

const fetchUnits = async (propertyId) => {
  const API_URL = config.API_URL || 'http://localhost:5000';
  try {
    const response = await axios.get(`${API_URL}/v2/unit/property/${propertyId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching units:", error);
    return [];
  }
};

const fetchVisitors = async (propertyId) => {
  const API_URL = config.API_URL || 'http://localhost:5000';
  try {
    const response = await axios.get(`${API_URL}/v2/visitor/property/${propertyId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching visitors:", error);
    return [];
  }

};

const fetchRequests = async (propertyId) => {
  const API_URL = config.API_URL || 'http://localhost:5000';
  try {
    const response = await axios.get(`${API_URL}/v2/request/property/${propertyId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });

    // Only show service requests
    const filteredData = response.data.filter(
      (r) => !["AmenityBooking", "GatePass", "WorkPermit"].includes(r.type)
    );

    return filteredData;
  } catch (error) {
    console.error("Error fetching requests:", error);
    return [];
  }
};

const fetchPayments = async (propertyId) => {
  const API_URL = config.API_URL || 'http://localhost:5000';
  try {
    const response = await axios.get(`${API_URL}/v2/payment/allocations/property/${propertyId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching recent payments:", error);
    return [];
  }
};

const fetchUnitsOutstandingBalances = async (propertyId) => {
  const API_URL = config.API_URL || 'http://localhost:5000';
  try {
    const response = await axios.get(`${API_URL}/v2/invoice/property/${propertyId}/unitsOutstanding`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching invoices:", error);
    return [];
  }
};

/* const fetchAccountsOutstandingBalances = async (propertyId) => {
  const API_URL = config.API_URL || 'http://localhost:5000';
  try {
    const response = await axios.get(`${API_URL}/v2/invoice/property/${propertyId}/accountsOutstanding`, {
      headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`},
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching accounts outstanding balances:", error);
    return [];
  }
}; */

export default Dashboard;